// lib/footfall/pulse.ts
import type { BrandCategory, PropertyFootfallSummary, PropertyVenue, PulseFlow, PulseLayer, PulseVenueAnchor, PulseVenueFeature, UnitStatus } from '#types/types';

/** The pilot centre Pulse opens on when the caller can see it. */
export const PULSE_PILOT_PROPERTY_ID = 'cmldnhzv20001v9eko4qcpr5j';

/** Runtime scope key: a user, organisation or impersonation switch invalidates every Pulse draft. */
export const ownerOf = (user: { dbUserId: string | null; organisationId: string | null; authenticatedOrganisationId?: string | null; isImpersonating?: boolean }) => `${user.dbUserId}:${user.organisationId}:${user.authenticatedOrganisationId ?? ''}:${user.isImpersonating}`;

export const PULSE_CATEGORIES: Record<BrandCategory, { colour: string; en: string; fr: string }> = {
  FASHION_AND_APPAREL: { colour: '#7B9CE0', en: 'Fashion', fr: 'Mode' },
  FOOD_AND_BEVERAGE: { colour: '#E8A35D', en: 'Food & beverage', fr: 'Restauration' },
  BEAUTY_AND_COSMETICS: { colour: '#D98BB5', en: 'Beauty', fr: 'Beauté' },
  HEALTH_AND_WELLNESS: { colour: '#6CC4A8', en: 'Health', fr: 'Santé' },
  ACCESSORIES_AND_JEWELRY: { colour: '#B79BE8', en: 'Accessories', fr: 'Accessoires' },
  SERVICES: { colour: '#9AA5B1', en: 'Services', fr: 'Services' },
  CULTURE_AND_LEISURE: { colour: '#E3C567', en: 'Culture & leisure', fr: 'Culture et loisirs' },
  ELECTRONICS_AND_MULTIMEDIA: { colour: '#6FB7D9', en: 'Electronics', fr: 'Électronique' },
  HOME_AND_DECORATION: { colour: '#C9A27E', en: 'Home', fr: 'Maison' },
  SPORT_AND_OUTDOOR: { colour: '#8BC36A', en: 'Sport', fr: 'Sport' },
  KIDS_AND_TOYS: { colour: '#F0907A', en: 'Kids', fr: 'Enfants' },
  AUTOMOTIVE: { colour: '#8E8E8E', en: 'Automotive', fr: 'Automobile' },
  OTHER: { colour: '#7A7A7A', en: 'Other', fr: 'Autre' },
};
export const PULSE_VACANT = { colour: '#3A3A3A', en: 'Vacant', fr: 'Vacant' };
/** A let shop our records cannot name: occupancy is known from the centre's directory, the trade is not. */
export const PULSE_UNKNOWN = { colour: '#5E5E5E', en: 'Unknown', fr: 'Inconnu' };
const TRAFFIC_RAMP = ['#232323', '#252424', '#282525', '#2C2626', '#312727'];
/** Passing-traffic colour: five equal percentile bands, quietest to busiest, kept near grey so the corridor aura is the map's only colour. */
export const trafficColour = (percentile: number | null) => percentile === null ? PULSE_NO_REVIEWS : TRAFFIC_RAMP[Math.min(4, Math.floor(percentile / 20))]!;
export const PULSE_STATUS: Record<UnitStatus | 'UNKNOWN', { colour: string; en: string; fr: string }> = {
  LEASED: { colour: '#4A5D80', en: 'Leased', fr: 'Loué' },
  UNDER_OFFER: { colour: '#E3C567', en: 'Under offer', fr: 'En négociation' },
  AVAILABLE: { colour: '#F28B82', en: 'Available', fr: 'Disponible' },
  UNKNOWN: { colour: '#3A3A3A', en: 'Unknown', fr: 'Inconnu' },
} as Record<UnitStatus | 'UNKNOWN', { colour: string; en: string; fr: string }>;
const REVIEW_RAMP = ['#253047', '#2F4775', '#3862C2', '#6E95E8', '#B4CBFF'];
export const PULSE_NO_REVIEWS = '#2A2A2A';
/** The five review bands as the map draws them, with each band's lowest review count for a centre whose busiest shop has `max`. */
export const reviewBands = (max: number) => REVIEW_RAMP.map((colour, i) => ({ colour, from: max > 0 ? Math.ceil(10 ** (i / REVIEW_RAMP.length * Math.log10(max + 1)) - 1) : 0 }));
export const maxReviews = (places: PulsePlace[]) => Math.max(0, ...places.map(p => p.shop?.reviews ?? 0));
/** 1st, 2nd, 3rd, 11th, 93rd; French takes "e". */
export const ordinal = (n: number, fr = false) => fr ? `${n}e` : `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
const ANCHOR_NAMES: Array<[RegExp, string, string]> = [[/garesncf/i, 'SNCF / RER station', 'Gare SNCF / RER'], [/gareroutiere/i, 'Bus station', 'Gare routière'], [/ParkingDuCentre/, 'Parking du Centre', 'Parking du Centre'], [/Parking_Aqueduc/, 'Parking Aqueduc', 'Parking Aqueduc'], [/parking/i, 'Car park', 'Parking']];
/** A readable name for each anchor; entrances are numbered per level in the venue's own order. */
export function anchorNames(anchors: PulseVenueAnchor[], levelName: (id: string | null) => string, fr = false): string[] {
  const counts = new Map<string, number>();
  return anchors.map(a => {
    if (a.kind === 'entrance') { const n = (counts.get(a.level ?? '') ?? 0) + 1; counts.set(a.level ?? '', n); return `${fr ? 'Entrée' : 'Entrance'} ${n} (${fr ? 'niv.' : 'level'} ${levelName(a.level)})`; }
    const named = ANCHOR_NAMES.find(([re]) => re.test(a.id));
    return named ? `${named[fr ? 2 : 1]}${a.level ? ` (${fr ? 'niv.' : 'level'} ${levelName(a.level)})` : ''}` : a.id;
  });
}

export type PulseShop = PropertyFootfallSummary['shops'][number];
/** The Brand record a shop links to, as the kernel registry holds it. */
export interface PulseBrand { id: string; name: string; category?: string | null; logoUrl?: string | null }
/** A venue shop with everything the map and its card show. */
export interface PulsePlace {
  feature: PulseVenueFeature;
  /** The linked brand's id and name; the name falls back to the centre's own label while the brand loads or when none links. */
  brandId: string | null;
  name: string | null;
  category: BrandCategory | 'VACANT' | 'UNKNOWN';
  status: UnitStatus | 'UNKNOWN';
  shop: PulseShop | null;
  logoUrl: string | null;
}

export const categoryEntry = (key: BrandCategory | 'VACANT' | 'UNKNOWN') => key === 'VACANT' ? PULSE_VACANT : key === 'UNKNOWN' ? PULSE_UNKNOWN : PULSE_CATEGORIES[key];
export const normalise = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const wholeWords = (haystack: string, needle: string) => needle.length >= 3 && ` ${haystack} `.includes(` ${needle} `);

/** The census shop a tenant trades as: its whole name must appear as words inside the shop's name. */
export function matchShop(tenantName: string | null, shops: PulseShop[]): PulseShop | null {
  const tenant = tenantName ? normalise(tenantName) : '';
  const hits = shops.filter(shop => wholeWords(normalise(shop.name), tenant));
  return hits.sort((a, b) => (b.reviews ?? -1) - (a.reviews ?? -1))[0] ?? null;
}

const compact = (value: string) => normalise(value).replace(/ /g, '');
/** Activity stays on the recorded tenant; a broad or ambiguous label has no measured profile. */
export function activityShop(tenantName: string | null, shops: PulseShop[], unitId?: string): PulseShop | null {
  const linked = unitId ? shops.flatMap(shop => shop.activityUnits?.filter(unit => unit.id === unitId).map(unit => ({ shop, unit })) ?? []) : [];
  if (linked.length) {
    if (linked.length !== 1) return null;
    const { shop, unit } = linked[0]!;
    const recorded = compact(unit.tenantName ?? ''), displayed = compact(tenantName ?? '');
    // The centre calls its recorded Five Pizza tenant "Five Pizza"; its linked Brand says "Five Pizza Original".
    // This verified name pair applies only to an explicit, unique unit binding, never the name-only fallback.
    const fivePizzaAlias = (recorded === 'fivepizza' && displayed === 'fivepizzaoriginal') || (recorded === 'fivepizzaoriginal' && displayed === 'fivepizza');
    // The centre's E1-005 directory names Sosta Oliveto "Le So - Sosta Oliveto".
    const sostaAlias = unit.id === 'E1-005' && ((recorded === 'lesosostaoliveto' && displayed === 'sostaoliveto') || (recorded === 'sostaoliveto' && displayed === 'lesosostaoliveto'));
    return shop.placeId && shop.activity && unit.tenantName && (recorded === displayed || fivePizzaAlias || sostaAlias) ? shop : null;
  }
  const key = tenantName ? compact(tenantName) : '';
  if (!key) return null;
  const exact = shops.filter(shop => compact(shop.name) === key);
  return exact.length === 1 && exact[0]!.placeId && exact[0]!.activity ? exact[0]! : null;
}
/**
 * The brand a venue label names among candidates. Exact (ignoring case, accents, spaces and punctuation) first. Loose
 * matching, one name starting with or containing the other, is for a short closed list such as a centre's own tenants,
 * where "Le So - Sosta Oliveto" can only mean Sosta Oliveto; it links only when a single brand fits best.
 */
export function linkBrand(label: string, candidates: Array<{ id: string; name: string }>, loose: boolean): string | null {
  const key = compact(label);
  if (!key) return null;
  const only = (list: Array<{ id: string; name: string }>) => new Set(list.map(c => c.id)).size === 1 ? list[0]!.id : null;
  const exact = candidates.filter(c => compact(c.name) === key);
  if (exact.length || !loose) return exact.length ? only(exact) : null;
  const fits = candidates.filter(c => { const k = compact(c.name); return (key.startsWith(k) && k.length >= 4) || (k.startsWith(key) && key.length >= 4) || (key.includes(k) && k.length >= 6); });
  const longest = Math.max(0, ...fits.map(c => compact(c.name).length));
  return only(fits.filter(c => compact(c.name).length === longest));
}

export function reviewColour(reviews: number | null, max: number): string {
  if (reviews === null || max <= 0) return PULSE_NO_REVIEWS;
  const t = Math.log10(reviews + 1) / Math.log10(max + 1);
  return REVIEW_RAMP[Math.min(REVIEW_RAMP.length - 1, Math.floor(t * REVIEW_RAMP.length))]!;
}

/** The venue's shops joined to their brands: a shop the centre lists by name is let, an unnamed one is empty. */
export function venuePlaces(venue: PropertyVenue, brands: Partial<Record<string, PulseBrand>>, shops: PulseShop[]): PulsePlace[] {
  return venue.features.filter(f => f.kind === 'unit').map(feature => {
    if (!feature.name) return { feature, brandId: null, name: null, category: 'VACANT', status: 'AVAILABLE', shop: null, logoUrl: null };
    const brand = feature.brandId ? brands[feature.brandId] : undefined;
    const category = brand ? (brand.category && brand.category in PULSE_CATEGORIES ? brand.category as BrandCategory : 'OTHER') : 'UNKNOWN';
    return { feature, brandId: feature.brandId ?? null, name: brand?.name ?? feature.name, category, status: feature.unitStatus ?? 'LEASED', shop: matchShop(feature.name, shops), logoUrl: brand?.logoUrl ?? null };
  });
}

export function placeColour(place: PulsePlace, layer: PulseLayer, maxReviews: number): string {
  if (layer === 'occupancy') return (PULSE_STATUS[place.status] ?? PULSE_STATUS.UNKNOWN).colour;
  if (layer === 'reviews') return reviewColour(place.shop?.reviews ?? null, maxReviews);
  if (layer === 'traffic') return trafficColour(place.feature.exposure?.percentile ?? null);
  return categoryEntry(place.category).colour;
}

/** One level of the venue as map features: shops coloured by layer, dimmed when a category filter excludes them. */
export function levelFeatures(venue: PropertyVenue, places: PulsePlace[], levelId: string | null, layer: PulseLayer, categories: string[], colours?: Record<string, string>) {
  const most = maxReviews(places);
  const shops = places.filter(p => p.feature.level === levelId).map((place, i) => ({
    type: 'Feature' as const, id: i,
    properties: { id: place.feature.id, unit: unitName(place.feature), kind: 'unit', label: place.name ?? '', logo: place.logoUrl ? 1 : 0, colour: colours?.[place.feature.id] ?? placeColour(place, layer, most), muted: categories.length > 0 && !categories.includes(place.category) },
    geometry: { type: 'MultiPolygon' as const, coordinates: place.feature.rings.map(ring => [[...ring, ring[0]!]]) },
  }));
  // Car parks are in the centre's plan as facilities, but drawn they are a large grey block over the street map.
  const others = venue.features.filter(f => f.kind !== 'unit' && (f.kind === 'building' || f.level === levelId) && !/parking/i.test(`${f.id} ${f.name ?? ''}`)).map((f, i) => ({
    type: 'Feature' as const, id: shops.length + i,
    properties: { id: f.id, kind: f.kind, label: f.kind === 'amenity' ? f.name ?? '' : '', colour: '', muted: false },
    geometry: { type: 'MultiPolygon' as const, coordinates: f.rings.map(ring => [[...ring, ring[0]!]]) },
  }));
  return { type: 'FeatureCollection' as const, features: [...others, ...shops] };
}

export function venueBounds(venue: PropertyVenue, levelId?: string | null, shopIds?: string[]): [[number, number], [number, number]] | null {
  const points = venue.features.filter(f => f.kind === 'unit' && (levelId === undefined || f.level === levelId) && (!shopIds?.length || shopIds.includes(f.id))).flatMap(f => f.rings.flat());
  if (!points.length) return null;
  return [[Math.min(...points.map(p => p[0])), Math.min(...points.map(p => p[1]))], [Math.max(...points.map(p => p[0])), Math.max(...points.map(p => p[1]))]];
}

/**
 * A level of the centre drawn flat, shops coloured by trade, in a width x height frame: the Plan cards'
 * picture and the Launch bar's. Null when the level has no shop to frame.
 */
export function venueLevelPaths(venue: PropertyVenue, places: PulsePlace[], levelId: string | null, width = 400, height = 300) {
  const bounds = venueBounds(venue, levelId);
  if (!bounds) return null;
  const [[west, south], [east, north]] = bounds;
  const kx = Math.cos((south + north) / 2 * Math.PI / 180);
  const w = (east - west) * kx, h = north - south, scale = Math.min((width - 20) / w, (height - 40) / h);
  const ox = (width - w * scale) / 2, oy = (height - h * scale) / 2;
  const path = (rings: Array<Array<[number, number]>>) => rings.map(r => r.map(([x, y], i) => `${i ? 'L' : 'M'}${(ox + (x - west) * kx * scale).toFixed(1)} ${(oy + (north - y) * scale).toFixed(1)}`).join('') + 'Z').join('');
  const byId = new Map(places.map(p => [p.feature.id, p]));
  return venue.features.filter(f => f.level === levelId && f.kind !== 'building').map(f => {
    const place = byId.get(f.id);
    return { id: f.id, d: path(f.rings), unit: f.kind === 'unit', fill: f.kind === 'unit' ? categoryEntry(place?.category ?? 'UNKNOWN').colour : '#2A2A2A', label: f.kind === 'unit' ? `${unitName(f)} · ${place?.name ?? 'vacant'}` : f.name ?? '' };
  });
}

/** The name a unit goes by: the landlord's own code from its merchandising plan, else the id on the centre's website. */
export const unitName = (feature: { id: string; code?: string | null }) => feature.code || feature.id;

export const ringCentre = (rings: Array<Array<[number, number]>>): [number, number] => { const all = rings.flat(); return [all.reduce((s, p) => s + p[0], 0) / all.length, all.reduce((s, p) => s + p[1], 0) / all.length]; };

/** Count per category, vacant last. */
export function tenantMix(places: PulsePlace[]) {
  const counts = new Map<BrandCategory | 'VACANT' | 'UNKNOWN', number>();
  for (const place of places) counts.set(place.category, (counts.get(place.category) ?? 0) + 1);
  return [...counts.entries()].sort(([a, x], [b, y]) => (a === 'VACANT' ? 1 : 0) - (b === 'VACANT' ? 1 : 0) || y - x);
}

export const busiestDay = (summary: PropertyFootfallSummary | null | undefined) =>
  summary?.station?.perDay.reduce<{ day: string; entries: number } | null>((best, day) => day.entries !== null && (!best || day.entries > best.entries) ? { day: day.day, entries: day.entries } : best, null) ?? null;

/** A euro amount, rounded to the euro; n/a when there is nothing to show. */
export const euro = (value: number | null, fr: boolean) => value === null ? 'n/a' : new Intl.NumberFormat(fr ? 'fr-FR' : 'en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);

/** Quiet to busy, for the Traffic layer's aura. */
export const FLOW_RAMP = ['rgba(76,48,140,0.25)', 'rgba(120,52,150,0.5)', 'rgba(186,52,120,0.7)', '#E2483F', '#F58A3C', '#FFE0A8'];
/** Where each FLOW_RAMP colour sits in heat-map density: closer together at the quiet end so side corridors still show, the last just under AURA_GAIN so the busiest corridor reaches it. */
export const FLOW_STOPS = [0.01, 0.03, 0.07, 0.14, 0.26, 0.42];
/** The aura's half-width on the ground. With points every AURA_STEP metres, a heat-map radius of this many metres and an
 * intensity of 3 * AURA_STEP / AURA_RADIUS, a lone corridor's density equals its share of the busiest corridor's walks
 * (a Gaussian kernel summed along a line), times AURA_GAIN, so the legend can read colours as walk counts. */
export const AURA_RADIUS = 8;
/** Below the calibrated 1: the network runs several near-parallel lines down a wide corridor, and their glows add up. */
export const AURA_GAIN = 0.45;
/** Door spurs are this short; a dead end longer than this is a real corridor end, like an entrance. */
const SPUR_METRES = 4;
/** A segment further than this from every shop runs outside the centre (car parks, the street). */
const OUTSIDE_METRES = 10;

/** Metres between the points an aura line is sampled into. */
export const AURA_STEP = 2;
/** Metres between two crests of the aura's ripple. */
const AURA_WAVE = 24;

/**
 * One level's corridor flows as the points of a heat aura. Door spurs and walks outside the centre are dropped, runs
 * between junctions joined into one line with its corners rounded, and the line sampled every AURA_STEP metres; each
 * point weighs its line's heat, the length-weighted mean of its segments' walks as a share of the centre's busiest segment.
 */
export function flowAura(flows: PulseFlow[], shops: Array<Array<[number, number]>>, maxWalks: number) {
  if (!flows.length || maxWalks <= 0) return [];
  const [ox, oy] = flows[0]!.path[0], kx = 111320 * Math.cos(oy * Math.PI / 180), ky = 110540;
  const m = ([x, y]: [number, number]) => [(x - ox) * kx, (y - oy) * ky] as const;
  const edges = shops.flatMap(ring => ring.map((p, i) => [m(p), m(ring[(i + 1) % ring.length]!)] as const));
  const nearShop = (p: [number, number]) => {
    const [x, y] = m(p);
    return edges.some(([[ax, ay], [bx, by]]) => {
      const dx = bx - ax, dy = by - ay, t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy || 1)));
      return Math.hypot(x - ax - t * dx, y - ay - t * dy) <= OUTSIDE_METRES;
    });
  };
  const key = (p: [number, number]) => `${p[0]},${p[1]}`;
  const length = (f: PulseFlow) => { const [ax, ay] = m(f.path[0]), [bx, by] = m(f.path[1]); return Math.hypot(bx - ax, by - ay); };
  let kept = flows.filter(f => nearShop([(f.path[0][0] + f.path[1][0]) / 2, (f.path[0][1] + f.path[1][1]) / 2]));
  const degree = () => { const d = new Map<string, number>(); for (const f of kept) for (const p of f.path) d.set(key(p), (d.get(key(p)) ?? 0) + 1); return d; };
  for (let pass = 0; pass < 2; pass++) { const d = degree(); kept = kept.filter(f => length(f) > SPUR_METRES || f.path.every(p => d.get(key(p))! > 1)); }

  const d = degree(), at = new Map<string, number[]>();
  kept.forEach((f, i) => { for (const p of f.path) at.set(key(p), [...(at.get(key(p)) ?? []), i]); });
  const used = new Set<number>(), lines: Array<{ coords: Array<[number, number]>; heat: number }> = [];
  const walk = (start: [number, number], first: number) => {
    const coords = [start];
    let node = start, seg = first, metres = 0, heat = 0;
    for (;;) {
      used.add(seg);
      const f = kept[seg]!, next = key(f.path[0]) === key(node) ? f.path[1] : f.path[0], len = length(f);
      metres += len; heat += len * f.walks / maxWalks;
      coords.push(next); node = next;
      const onward = d.get(key(node)) === 2 ? at.get(key(node))!.find(i => !used.has(i)) : undefined;
      if (onward === undefined) break;
      seg = onward;
    }
    lines.push({ coords, heat: metres ? heat / metres : 0 });
  };
  for (const [k, segs] of at) if (d.get(k) !== 2) for (const s of segs) if (!used.has(s)) { const f = kept[s]!; walk(key(f.path[0]) === k ? f.path[0] : f.path[1], s); }
  kept.forEach((f, i) => { if (!used.has(i)) walk(f.path[0], i); });
  // Chaikin corner cutting, twice: the network's polyline corners become curves, the ends stay where they are.
  const smooth = (c: Array<[number, number]>) => c.length < 3 ? c : [c[0]!, ...c.slice(0, -1).flatMap((p, i) => { const q = c[i + 1]!; return [[p[0] * 0.75 + q[0] * 0.25, p[1] * 0.75 + q[1] * 0.25], [p[0] * 0.25 + q[0] * 0.75, p[1] * 0.25 + q[1] * 0.75]] as Array<[number, number]>; }).slice(1, -1), c[c.length - 1]!];
  // `phase` is the point's place along its line, one turn every AURA_WAVE metres, so a ripple can travel the corridor.
  return lines.flatMap(l => {
    const points: Array<{ type: 'Feature'; properties: { heat: number; phase: number }; geometry: { type: 'Point'; coordinates: [number, number] } }> = [];
    let along = 0, nextAt = 0;
    smooth(smooth(l.coords)).forEach((p, i, c) => {
      const q = c[i + 1];
      if (!q) return;
      const [ax, ay] = m(p), [bx, by] = m(q), len = Math.hypot(bx - ax, by - ay);
      for (; nextAt < along + len; nextAt += AURA_STEP) {
        const t = (nextAt - along) / len;
        points.push({ type: 'Feature', properties: { heat: Math.round(l.heat * 1000) / 1000, phase: Math.round(nextAt / AURA_WAVE * 2 * Math.PI * 100) / 100 },
          geometry: { type: 'Point', coordinates: [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t] } });
      }
      along += len;
    });
    return points;
  });
}
